import { useMemo, useState } from 'react'
import { Card, Chip, EmptyState, LoadingRows, StatCard, secondaryBtn } from '../family-finance/shared'
import type { PlannerEvent } from '../../types/family-planner'
import {
  eventDaySpan,
  fmtDateRange,
  fmtDay,
  fmtEventTime,
  parseLocal,
  todayIndex,
} from '../../lib/family-planner-analytics'
import { CATEGORY_META, type PlannerState } from './usePlanner'

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function monthLabel(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

export default function CalendarTab({ state }: { state: PlannerState }) {
  const { loading, events, conflicts, windows, calendars, members, calendarById } = state
  const [view, setView] = useState<'month' | 'agenda'>('month')
  const [monthOffset, setMonthOffset] = useState(0)
  const [memberFilter, setMemberFilter] = useState<string | null>(null)
  const [selectedDay, setSelectedDay] = useState<number | null>(null)

  const shown = useMemo(
    () => (memberFilter ? events.filter((e) => e.memberIds.includes(memberFilter)) : events),
    [events, memberFilter],
  )

  const byDay = useMemo(() => {
    const map = new Map<number, PlannerEvent[]>()
    for (const e of shown) {
      const [first, last] = eventDaySpan(e)
      for (let d = first; d <= last; d++) {
        map.set(d, [...(map.get(d) ?? []), e])
      }
    }
    for (const list of map.values()) list.sort((a, b) => a.start.localeCompare(b.start))
    return map
  }, [shown])

  const monthStart = useMemo(() => {
    const d = new Date()
    d.setDate(1)
    d.setMonth(d.getMonth() + monthOffset)
    d.setHours(0, 0, 0, 0)
    return d
  }, [monthOffset])

  const gridDays = useMemo(() => {
    const first = new Date(monthStart)
    const gridStart = new Date(first)
    gridStart.setDate(first.getDate() - first.getDay())
    return Array.from({ length: 42 }, (_, i) => {
      const date = new Date(gridStart)
      date.setDate(gridStart.getDate() + i)
      return date
    })
  }, [monthStart])

  const today = todayIndex()
  const idxOf = (d: Date) => Math.floor(new Date(d).setHours(0, 0, 0, 0) / 86400000)

  const thisWeekCount = useMemo(() => {
    const dow = new Date().getDay()
    const weekEnd = today + (6 - dow)
    return shown.filter((e) => {
      const [first, last] = eventDaySpan(e)
      return last >= today && first <= weekEnd
    }).length
  }, [shown, today])

  const agenda = useMemo(() => {
    const groups: { day: number; date: Date; events: PlannerEvent[] }[] = []
    for (let d = today; d <= today + 45; d++) {
      const list = byDay.get(d)
      if (!list?.length) continue
      const date = new Date()
      date.setHours(0, 0, 0, 0)
      date.setDate(date.getDate() + (d - today))
      groups.push({ day: d, date, events: list })
    }
    return groups
  }, [byDay, today])

  const bestWindow = windows[0]

  if (loading) return <LoadingRows rows={6} />

  const selectedEvents = selectedDay !== null ? byDay.get(selectedDay) ?? [] : []

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="This week" value={String(thisWeekCount)} sub="events across all calendars" />
        <StatCard
          label="Conflicts"
          value={String(conflicts.length)}
          sub={conflicts.length ? 'overlapping commitments' : 'nothing overlapping'}
          subTone={conflicts.length ? 'red' : 'green'}
        />
        <StatCard
          label="Calendars"
          value={`${calendars.filter((c) => c.enabled).length}/${calendars.length}`}
          sub="showing in this view"
        />
        <StatCard
          label="Next open window"
          value={bestWindow ? `${bestWindow.nights}n` : '—'}
          sub={bestWindow ? fmtDateRange(bestWindow.start, bestWindow.end) : 'no clear stretch'}
          subTone={bestWindow ? 'green' : 'slate'}
        />
      </div>

      {/* Filters + view toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setMemberFilter(null)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              memberFilter === null
                ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white'
                : 'text-slate-500 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
            }`}
          >
            Everyone
          </button>
          {members.map((m) => (
            <button
              key={m.id}
              onClick={() => setMemberFilter(memberFilter === m.id ? null : m.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                memberFilter === m.id
                  ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white'
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
              }`}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
              {m.name}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 bg-slate-200/60 dark:bg-slate-700/60 rounded-lg p-1">
          {(['month', 'agenda'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-3 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                view === v
                  ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white'
                  : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {conflicts.length > 0 && (
        <Card title="Conflicts" subtitle="Same person, overlapping times">
          <div className="space-y-2">
            {conflicts.map((c) => {
              const name = members.find((m) => m.id === c.memberId)?.name ?? c.memberId
              return (
                <div
                  key={c.id}
                  className="flex flex-wrap items-center gap-2 text-sm bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2"
                >
                  <Chip tone="red">{name}</Chip>
                  <span className="text-slate-900 dark:text-white">{c.a.title}</span>
                  <span className="text-slate-400">×</span>
                  <span className="text-slate-900 dark:text-white">{c.b.title}</span>
                  <span className="text-slate-500 dark:text-slate-400 text-xs">
                    {fmtDay(parseLocal(c.a.start))} · overlaps {c.overlapMinutes} min
                  </span>
                </div>
              )
            })}
          </div>
        </Card>
      )}

      {view === 'month' ? (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-slate-900 dark:text-white font-semibold">{monthLabel(monthStart)}</h3>
            <div className="flex items-center gap-2">
              <button className={secondaryBtn} onClick={() => setMonthOffset((m) => m - 1)}>
                ←
              </button>
              <button className={secondaryBtn} onClick={() => setMonthOffset(0)}>
                Today
              </button>
              <button className={secondaryBtn} onClick={() => setMonthOffset((m) => m + 1)}>
                →
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-px text-center mb-1">
            {WEEKDAY_LABELS.map((d) => (
              <div key={d} className="text-slate-500 dark:text-slate-400 text-xs font-medium py-1">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px bg-slate-200 dark:bg-slate-700 rounded-lg overflow-hidden">
            {gridDays.map((date) => {
              const index = idxOf(date)
              const dayEvents = byDay.get(index) ?? []
              const inMonth = date.getMonth() === monthStart.getMonth()
              const isToday = index === today
              const isSelected = index === selectedDay
              return (
                <button
                  key={index}
                  onClick={() => setSelectedDay(isSelected ? null : index)}
                  className={`min-h-[86px] p-1.5 text-left align-top transition-colors ${
                    inMonth ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-800/40'
                  } ${isSelected ? 'ring-2 ring-inset ring-blue-500' : 'hover:bg-slate-100 dark:hover:bg-slate-700/50'}`}
                >
                  <span
                    className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs mb-1 ${
                      isToday
                        ? 'bg-blue-600 text-white font-semibold'
                        : inMonth
                          ? 'text-slate-700 dark:text-slate-200'
                          : 'text-slate-400 dark:text-slate-600'
                    }`}
                  >
                    {date.getDate()}
                  </span>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 3).map((e) => (
                      <div key={`${e.id}-${index}`} className="flex items-center gap-1">
                        <span
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: calendarById(e.calendarId)?.color ?? '#94a3b8' }}
                        />
                        <span className="text-[10px] leading-tight text-slate-600 dark:text-slate-300 truncate">
                          {e.title}
                        </span>
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-slate-400 dark:text-slate-500 pl-2.5">
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          {selectedDay !== null && (
            <div className="mt-4 border-t border-slate-200 dark:border-slate-700 pt-4">
              {selectedEvents.length === 0 ? (
                <EmptyState icon="🗓️" message="Nothing scheduled that day." />
              ) : (
                <div className="space-y-2">
                  {selectedEvents.map((e) => (
                    <EventRow key={e.id} event={e} state={state} />
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      ) : (
        <Card title="Agenda" subtitle="Next 45 days, all enabled calendars">
          {agenda.length === 0 ? (
            <EmptyState icon="🗓️" message="Nothing on the calendar in the next 45 days." />
          ) : (
            <div className="space-y-5">
              {agenda.map((group) => (
                <div key={group.day}>
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`text-sm font-semibold ${
                        group.day === today ? 'text-blue-600 dark:text-blue-400' : 'text-slate-900 dark:text-white'
                      }`}
                    >
                      {group.day === today ? 'Today' : fmtDay(group.date)}
                    </span>
                    <span className="text-xs text-slate-400 dark:text-slate-500">
                      {group.events.length} event{group.events.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {group.events.map((e) => (
                      <EventRow key={`${group.day}-${e.id}`} event={e} state={state} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  )
}

function EventRow({ event, state }: { event: PlannerEvent; state: PlannerState }) {
  const calendar = state.calendarById(event.calendarId)
  const meta = CATEGORY_META[event.category] ?? CATEGORY_META.other
  return (
    <div className="flex items-start gap-3 bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2">
      <span
        className="w-1 self-stretch rounded-full flex-shrink-0"
        style={{ backgroundColor: calendar?.color ?? '#94a3b8' }}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-slate-900 dark:text-white text-sm font-medium">
            {meta.icon} {event.title}
          </span>
          {event.origin !== 'calendar' && event.origin !== 'manual' && (
            <Chip tone={event.origin === 'gmail' ? 'blue' : 'green'}>
              from {event.origin === 'gmail' ? 'Gmail' : 'WhatsApp'}
            </Chip>
          )}
          {event.mergedFrom?.length ? <Chip tone="slate">merged ×{event.mergedFrom.length + 1}</Chip> : null}
        </div>
        <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
          {fmtEventTime(event)}
          {event.location ? ` · ${event.location}` : ''}
          {calendar ? ` · ${calendar.name}` : ''}
        </p>
      </div>
      <div className="flex flex-wrap gap-1 justify-end">
        {event.memberIds.map((id) => {
          const member = state.memberById(id)
          if (!member) return null
          return (
            <span
              key={id}
              title={member.name}
              className="w-5 h-5 rounded-full text-[10px] font-semibold text-white flex items-center justify-center"
              style={{ backgroundColor: member.color }}
            >
              {member.name.charAt(0)}
            </span>
          )
        })}
      </div>
    </div>
  )
}
